import { SERIES } from "@/components/visual/chart-colors";
import type { BarDatum, ColumnDatum, Segment, SparkVisualData } from "@/components/visual/charts";
import type { VisualTable } from "@/components/visual/visual-table";
import type { VerdictLevel } from "@/components/visual/VerdictLine";
import { addDays } from "@/engine/date";
import { formatDate } from "@/lib/date-format";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import type { Count, TaskSummary } from "./task-query";

/*
 * 분석 탭 차트 어댑터(순수 함수). 리포트 어댑터와 같은 모양을 만들어 두 화면이 같은 차트 언어를 쓴다.
 */

/** 진행 순서(완료 → 진행 중 → 시작 전 → 막힘 → 미정)로 쌓는다. "" = 상태 미정 */
export const STATUS_STACK_ORDER = ["done", "inProgress", "notStarted", "blocked", ""] as const;
export type StatusStackKey = (typeof STATUS_STACK_ORDER)[number];

const STATUS_COLOR: Record<StatusStackKey, string> = {
  done: SERIES.good,
  inProgress: SERIES.actual,
  notStarted: SERIES.info,
  blocked: SERIES.bad,
  "": SERIES.empty,
};

function statusLabel(key: StatusStackKey): string {
  return key ? STATUS_LABEL[key] : "상태 미정";
}

function pct(value: number, total: number): number {
  return total ? Math.round((value / total) * 100) : 0;
}

export function statusSegments(sum: TaskSummary): Segment<StatusStackKey>[] {
  return STATUS_STACK_ORDER.map((key) => ({
    key,
    label: statusLabel(key),
    value: sum.byStatus.find((c) => c.key === key)?.value ?? 0,
    color: STATUS_COLOR[key],
  }));
}

export function statusTable(sum: TaskSummary, onPick?: (key: StatusStackKey) => void): VisualTable {
  const segments = statusSegments(sum);
  return {
    ariaLabel: "상태 분포 표",
    columns: [
      { key: "status", label: "상태" },
      { key: "count", label: "Task", numeric: true },
      { key: "share", label: "비율", numeric: true },
    ],
    rows: segments.map((s) => ({
      key: s.key || "unset",
      cells: [s.label, `${s.value}개`, `${pct(s.value, sum.total)}%`],
      onActivate: onPick && s.value ? () => onPick(s.key) : undefined,
      activateLabel: onPick ? "Task 보기" : undefined,
    })),
  };
}

/** 분석 카드 막대에 한 번에 보여주는 항목 수. 나머지는 크게 보기. */
export const DISTRIBUTION_PREVIEW = 5;

/**
 * 분포를 카드와 크게 보기에 나눠 담는다.
 * 항목이 미리보기보다 많으면 건수 내림차순으로 정렬하고, 같거나 적으면 원래 순서(우선순위·버킷)를 유지한다.
 */
export function distributionView(counts: readonly Count[], limit = DISTRIBUTION_PREVIEW): { all: Count[]; preview: Count[] } {
  if (counts.length <= limit) return { all: [...counts], preview: [...counts] };
  const all = counts
    .map((c, index) => ({ c, index }))
    .sort((a, b) => b.c.value - a.c.value || a.index - b.index)
    .map((x) => x.c);
  return { all, preview: all.slice(0, limit) };
}

/** 분포 막대: 미정(키 "")은 중립색, 나머지는 한 가지 색(장식 팔레트를 쓰지 않는다). */
export function countBars(counts: readonly Count[], onPick?: (key: string) => void): BarDatum[] {
  return counts.map((c) => ({
    key: c.key || "__none",
    label: c.label,
    value: c.value,
    valueLabel: `${c.value}개`,
    color: c.key === "" || c.value === 0 ? SERIES.empty : SERIES.actual,
    ariaLabel: `${c.label} ${c.value}개`,
    onClick: onPick && c.value ? () => onPick(c.key) : undefined,
  }));
}

export function countTable(title: string, counts: readonly Count[], onPick?: (key: string) => void): VisualTable {
  const total = counts.reduce((a, c) => a + c.value, 0);
  return {
    ariaLabel: `${title} 표`,
    columns: [
      { key: "label", label: title },
      { key: "count", label: "Task", numeric: true },
      { key: "share", label: "비율", numeric: true },
    ],
    rows: counts.map((c) => ({
      key: c.key || "__none",
      cells: [c.label, `${c.value}개`, `${pct(c.value, total)}%`],
      onActivate: onPick && c.value ? () => onPick(c.key) : undefined,
      activateLabel: onPick ? "Task 보기" : undefined,
    })),
  };
}

/** 주 시작일(월) → "09/28 주" 콜아웃 머리 */
function weekTitle(weekStart: string): string {
  return `${formatDate(weekStart, "numeric")} ~ ${formatDate(addDays(weekStart, 6), "short")}`;
}

/** 최근 8주 주간 완료 수(처리량). 완료 상태색(초록)을 쓴다. */
export function weeklyColumns(sum: TaskSummary): ColumnDatum[] {
  return sum.completedByWeek.map((w) => ({
    key: w.weekStart,
    label: formatDate(w.weekStart, "short"),
    calloutTitle: weekTitle(w.weekStart),
    value: w.value,
    valueLabel: `${w.value}개`,
    legend: "완료",
    color: SERIES.good,
  }));
}

export function weeklyTable(sum: TaskSummary): VisualTable {
  return {
    ariaLabel: "주간 완료 추이 표",
    columns: [
      { key: "week", label: "주" },
      { key: "count", label: "완료", numeric: true },
    ],
    rows: sum.completedByWeek.map((w) => ({ key: w.weekStart, cells: [weekTitle(w.weekStart), `${w.value}개`] })),
  };
}

/**
 * 완료율 추이(최근 8주 말 기준 누적). 마지막 점이 지금의 완료율과 같아 KPI 숫자와 선이 어긋나지 않는다.
 * 주 i 말의 완료 수 = 지금 완료 수 − 그 뒤 주들에 완료된 수.
 */
export function completionTrend(sum: TaskSummary): SparkVisualData | undefined {
  if (!sum.total || sum.completedByWeek.length < 2) return undefined;
  const weeks = sum.completedByWeek;
  const points = weeks.map((w, i) => {
    const later = weeks.slice(i + 1).reduce((a, x) => a + x.value, 0);
    const value = pct(Math.max(0, sum.done - later), sum.total);
    return { x: i, y: value, label: `${formatDate(w.weekStart, "short")} 주 ${value}%` };
  });
  return { points };
}

export function completionTrendLabel(sum: TaskSummary): string {
  const data = completionTrend(sum);
  return `완료율 추이(최근 8주): ${(data?.points ?? []).map((p) => p.label).join(", ")}`;
}

/**
 * 분석 탭 판정. 리포트 판정(reportLevel)의 Task 기준과 같다: 막힌 Task나 마감 넘긴 Task가 있으면 주의.
 */
export function taskLevel(sum: Pick<TaskSummary, "blocked" | "overdue">): { level: VerdictLevel; reasons: string[] } {
  const reasons: string[] = [];
  if (sum.blocked > 0) reasons.push(`막힌 Task ${sum.blocked}개`);
  if (sum.overdue > 0) reasons.push(`마감 넘긴 Task ${sum.overdue}개`);
  if (reasons.length) return { level: "watch", reasons };
  return { level: "good", reasons: ["막히거나 마감을 넘긴 Task 없음"] };
}
